import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Search } from 'lucide-react';
import { api, type BrowsedIssue } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { EmptyState, ErrorNote, Field } from '../../components/kit';
import { Attachments, type Attached } from '../../components/attachments';

export function IssuePicker({ tenant, onStarted }: { tenant: string | null; onStarted: () => void }) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const issues = useQuery({
    queryKey: ['browse', tenant, term],
    queryFn: () =>
      api.get<{ issues: BrowsedIssue[] }>(
        `/api/sources/browse?q=${encodeURIComponent(term)}${tenant ? `&tenant=${tenant}` : ''}`,
      ),
    enabled: open,
  });
  const start = useMutation({
    mutationFn: (issue: BrowsedIssue) => api.post(`/api/sources/${issue.sourceId}/start/${issue.externalId}`),
    onSuccess: onStarted,
  });

  return (
    <div className="relative">
      <Button variant="outline" size="sm" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Search className="size-3.5" aria-hidden /> Pick a tracker issue
      </Button>
      {open && (
        <div className="absolute right-0 z-30 mt-2 w-[min(34rem,90vw)] rounded-[7px] border border-rule bg-panel p-3 shadow-[var(--shadow-pop)]">
          <Input
            autoFocus
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder="Search by key, title or status"
          />
          <div className="mt-2 max-h-72 divide-y divide-rule overflow-auto">
            {issues.isFetching && <Skeleton className="h-10 w-full" />}
            {(issues.data?.issues ?? []).map((issue) => (
              <div
                key={`${issue.sourceId}:${issue.externalId}`}
                className="flex items-start justify-between gap-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-[13px]">
                    <span className="data font-semibold text-ink">{issue.externalId}</span> {issue.title}
                  </p>
                  <p className="label-caps mt-0.5">
                    {issue.status} · {issue.sourceName}
                  </p>
                </div>
                <Button size="sm" disabled={start.isPending} onClick={() => start.mutate(issue)}>
                  Start
                </Button>
              </div>
            ))}
            {issues.isFetched && (issues.data?.issues ?? []).length === 0 && (
              <p className="py-3 text-[12px] text-ink-2">
                No matching issue — or this tenant has no tracker configured yet.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Queueing by hand is a deliberate act, not furniture: it gets a dialog with room
 * for the detail an unattended agent actually needs, instead of a permanent form
 * competing with the queue it writes into.
 */
export function NewTaskDialog({
  open,
  projects,
  onClose,
  onCreated,
}: {
  open: boolean;
  projects: { id: string; key: string; tenantId: string; handlers?: string[] }[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [projectId, setProjectId] = useState(projects[0]?.id ?? '');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [images, setImages] = useState<Attached[]>([]);
  const selected = projects.find((project) => project.id === projectId) ?? projects[0];
  // The status a hand-queued task starts in. Nothing runs it unless the tenant
  // has a handler for that status, or a "*" one.
  const runnable =
    selected === undefined ||
    (selected.handlers ?? []).some((status) => status === '*' || status.toLowerCase() === 'todo');

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/tasks', {
        tenantId: selected?.tenantId,
        projectKey: selected?.key,
        title,
        body,
        attachmentIds: images.map((image) => image.id),
      }),
    onSuccess: () => {
      setTitle('');
      setBody('');
      setImages([]);
      onCreated();
    },
  });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>New task</DialogTitle>
          <DialogDescription>
            It joins the queue for the chosen repository. Nobody will be watching while it runs, so say
            what "done" means here rather than expecting a question later.
          </DialogDescription>
        </DialogHeader>

        {projects.length === 0 ? (
          <EmptyState
            title="No repositories yet"
            hint="A task needs a repository to work in. Add one under Tenants first."
          />
        ) : (
          <form
            className="grid gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
          >
            {!runnable && (
              <p
                role="alert"
                className="rounded-[5px] border border-warn/30 bg-warn-soft px-2.5 py-2 text-[12px] text-warn"
              >
                This tenant has no handler for a queued task, so nothing will run it. Add one under
                Tenants → Handlers — a "*" handler covers every status.
              </p>
            )}
            <Field label="Repository">
              <Select value={selected?.id} onValueChange={setProjectId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Repository" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.key}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="What should the agent do?" htmlFor="task-title">
              <Input
                id="task-title"
                required
                autoFocus
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Add a format:check script that verifies formatting without writing"
              />
            </Field>
            <Field
              label="Detail"
              htmlFor="task-body"
              hint="Nobody will be watching, so say how you would know it is done."
            >
              <Textarea
                id="task-body"
                value={body}
                onChange={(event) => setBody(event.target.value)}
                className="min-h-32"
                placeholder="Acceptance criteria, the files involved, constraints, how to verify it…"
              />
            </Field>
            <Field label="Screenshots" hint="A picture of the bug says more than a paragraph about it.">
              <Attachments tenantId={selected?.tenantId} value={images} onChange={setImages} />
            </Field>
            <ErrorNote error={create.error} />
            {/* Fastened to the bottom of the dialog: however long the brief gets,
                the way out of the form is never further away than the screen. */}
            <div className="sticky bottom-0 -mx-6 -mb-6 flex justify-end gap-2 border-t border-rule bg-background px-6 py-3">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending || !title.trim()}>
                Queue task
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
